import type { LessonRuleFormulaRow, LessonRulePayload } from "site/services/app.service";

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

export interface LessonRuleModalClasses {
  blockRu: string;
  proseCard: string;
  formulasDeck: string;
  formulasDeckTitle: string;
  formulaSection: string;
  formulaRule: string;
  exampleBlock: string;
  exampleArabic: string;
  inlineRuleRef: string;
  refMissing: string;
}

export type LessonRuleRefResolver = (id: string) => { ok: boolean; label: string };

export function expandInlineRuleRefs(
  ru: string,
  resolve: LessonRuleRefResolver,
  buttonClass: string,
  missingClass: string
): string {
  const re = /\[\[rule:([a-zA-Z0-9_]+)\]\]/g;
  let last = 0;
  const out: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(ru)) !== null) {
    out.push(escapeHtml(ru.slice(last, m.index)));
    const id = m[1]!;
    const r = resolve(id);
    if (r.ok) {
      out.push(
        `<button type="button" class="${buttonClass}" data-grammar-rule-ref="${escapeHtml(id)}" dir="rtl" lang="ar">${escapeHtml(r.label)}</button>`
      );
    } else {
      out.push(`<span class="${missingClass}" title="unknown rule">${escapeHtml(`[[rule:${id}]]`)}</span>`);
    }
    last = m.index + m[0].length;
  }
  out.push(escapeHtml(ru.slice(last)));
  return out.join("");
}

function isArabicCodePoint(cp: number): boolean {
  return (
    (cp >= 0x0600 && cp <= 0x06ff) ||
    (cp >= 0x0750 && cp <= 0x077f) ||
    (cp >= 0x08a0 && cp <= 0x08ff) ||
    (cp >= 0xfb50 && cp <= 0xfdff) ||
    (cp >= 0xfe70 && cp <= 0xfeff)
  );
}

function isArabicIndicDigit(cp: number): boolean {
  return cp >= 0x0660 && cp <= 0x0669;
}

function isArabicMarkCodePoint(cp: number): boolean {
  return (cp >= 0x0610 && cp <= 0x061a) || (cp >= 0x064b && cp <= 0x065f) || (cp >= 0x06d6 && cp <= 0x06ed);
}

function isArabicTextCore(cp: number): boolean {
  return isArabicCodePoint(cp) && !isArabicIndicDigit(cp);
}

function mergeAdjacentScriptChunks(chunks: { ar: boolean; text: string }[]): { ar: boolean; text: string }[] {
  const out: { ar: boolean; text: string }[] = [];
  for (const c of chunks) {
    const last = out[out.length - 1];
    if (last && last.ar === c.ar) last.text += c.text;
    else out.push({ ar: c.ar, text: c.text });
  }
  return out;
}

export function buildLessonRuleStackedHtml(
  text: string,
  lineArClass: string,
  lineRuClass: string
): string {
  const chunks: { ar: boolean; text: string }[] = [];
  let buf = "";
  let mode: "ar" | "ru" | null = null;

  function flush(): void {
    if (!buf) return;
    chunks.push({ ar: mode === "ar", text: buf });
    buf = "";
    mode = null;
  }

  let i = 0;
  while (i < text.length) {
    const cp = text.codePointAt(i)!;
    const len = cp > 0xffff ? 2 : 1;
    const ch = String.fromCodePoint(cp);
    const core = isArabicTextCore(cp);
    const mark = isArabicMarkCodePoint(cp);

    if (core) {
      if (mode !== "ar") {
        flush();
        mode = "ar";
      }
      buf += ch;
    } else if (mark) {
      if (mode === "ar") buf += ch;
      else {
        if (mode !== "ru") {
          flush();
          mode = "ru";
        }
        buf += ch;
      }
    } else {
      if (mode !== "ru") {
        flush();
        mode = "ru";
      }
      buf += ch;
    }
    i += len;
  }
  flush();

  return mergeAdjacentScriptChunks(chunks)
    .map((c) => ({ ...c, text: c.text }))
    .filter((c) => c.text.length > 0)
    .map((c) =>
      c.ar
        ? `<div class="${lineArClass}" dir="rtl" lang="ar">${escapeHtml(c.text)}</div>`
        : `<div class="${lineRuClass}" dir="ltr" lang="ru">${escapeHtml(c.text)}</div>`
    )
    .join("");
}

function buildFormulaSectionsHtml(rows: LessonRuleFormulaRow[], c: LessonRuleModalClasses): string {
  const parts = rows.map((row) => {
    const formula = row.formula;
    const example = row.example;

    let inner = `<div class="${c.formulaRule}" dir="rtl" lang="ar">${escapeHtml(formula)}</div>`;

    if (example) {
      inner += `<div class="${c.exampleBlock}">
        <div class="${c.exampleArabic}" dir="rtl" lang="ar">${escapeHtml(example)}</div>
      </div>`;
    }

    return `<div class="${c.formulaSection}">${inner}</div>`;
  });

  return parts.join("");
}

export function buildLessonRuleModalHtml(
  payload: LessonRulePayload | null,
  css: LessonRuleModalClasses,
  resolveRef?: LessonRuleRefResolver
): string {
  if (!payload) return "";
  const ru = payload.ru;
  const rows = payload.formulaRows;
  const hasRu = ru.length > 0;
  const hasFormulas = rows.length > 0;
  const useInlineRefs = !!(resolveRef && ru.includes("[[rule:"));

  if (hasRu && !hasFormulas) {
    if (useInlineRefs) {
      return `<div class="${css.proseCard}"><div class="${css.blockRu}" dir="ltr" lang="ru">${expandInlineRuleRefs(
        ru,
        resolveRef!,
        css.inlineRuleRef,
        css.refMissing
      )}</div></div>`;
    }
    return buildLessonRuleStackedHtml(ru, css.formulaRule, css.blockRu);
  }

  const parts: string[] = [];

  if (hasRu) {
    const ruInner = useInlineRefs
      ? expandInlineRuleRefs(ru, resolveRef!, css.inlineRuleRef, css.refMissing)
      : escapeHtml(ru);
    parts.push(
      `<div class="${css.proseCard}"><div class="${css.blockRu}" dir="ltr" lang="ru">${ruInner}</div></div>`
    );
  }

  if (hasFormulas) {
    const deckInner = `
      <div class="${css.formulasDeckTitle}" lang="ru">Формулы и примеры</div>
      ${buildFormulaSectionsHtml(rows, css)}
    `;
    parts.push(`<div class="${css.formulasDeck}">${deckInner}</div>`);
  }

  return parts.join("");
}
